import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Container, Typography, Box, Grid, Button, CardMedia,
  TextField, Select, MenuItem, FormControl, InputLabel, Chip,
  InputAdornment, CircularProgress, ToggleButton, ToggleButtonGroup,
  Slider,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import SailingIcon from '@mui/icons-material/Sailing';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import PeopleIcon from '@mui/icons-material/People';
import Luggage from '@mui/icons-material/Luggage';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SpeedIcon from '@mui/icons-material/Speed';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SortIcon from '@mui/icons-material/Sort';
import TuneIcon from '@mui/icons-material/Tune';
import StarIcon from '@mui/icons-material/Star';
import { TZ, todayMayotte } from '../../utils/date';

/* ── Design tokens ─────────────────────────────────────────── */
const T = {
  purple: '#552583',
  purpleDark: '#3d1a61',
  gold: '#FDB927',
  bg: '#E4F0F6',
  white: '#ffffff',
  ink: '#1a1a1a',
  sub: '#4a4a4a',
  muted: '#7a7a7a',
  border: 'rgba(85,37,131,0.08)',
  radius: { sm: '8px', md: '10px', btn: '12px', card: '20px', pill: '40px' },
  transition: '0.15s ease',
  shadow: '0 1px 4px rgba(0,0,0,0.06)',
  shadowHover: '0 6px 20px rgba(0,0,0,0.1)',
};

const btnPrimary = {
  bgcolor: T.purple, color: 'white', fontWeight: 700,
  borderRadius: T.radius.btn, textTransform: 'none',
  transition: T.transition,
  boxShadow: 'none',
  '&:hover': { bgcolor: T.purpleDark, boxShadow: 'none' },
};

const btnGold = {
  bgcolor: T.gold, color: T.purple, fontWeight: 700,
  borderRadius: T.radius.btn, textTransform: 'none',
  transition: T.transition,
  boxShadow: 'none',
  '&:hover': { bgcolor: '#e5a520', boxShadow: 'none' },
};

const vehicleTypes = [
  { value: 'auto',   label: 'Voitures', icon: <DirectionsCarIcon /> },
  { value: 'moto',   label: 'Motos',    icon: <TwoWheelerIcon /> },
  { value: 'bateau', label: 'Bateaux',  icon: <SailingIcon /> },
];

const heroImages = {
  auto:   'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920',
  moto:   'https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=1920',
  bateau: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920',
};

const heroTitles = {
  auto:   { overline: 'LOCATION DE VOITURES', title: 'votre voiture',  subtitle: 'Des véhicules pour tous vos besoins à Mayotte' },
  moto:   { overline: 'LOCATION DE MOTOS',    title: 'votre moto',     subtitle: 'Explorez Mayotte en deux roues' },
  bateau: { overline: 'LOCATION DE BATEAUX',  title: 'votre bateau',   subtitle: 'Naviguez dans le lagon de Mayotte' },
};

const advantages = [
  { icon: <VerifiedUserIcon sx={{ fontSize: 22, color: T.purple }} />, title: 'Assurance incluse',    desc: 'Tous nos véhicules sont assurés' },
  { icon: <EventAvailableIcon sx={{ fontSize: 22, color: T.purple }} />, title: 'Annulation gratuite', desc: "Jusqu'à 24h avant la prise" },
  { icon: <SupportAgentIcon sx={{ fontSize: 22, color: T.purple }} />, title: 'Assistance 7j/7',     desc: 'Support disponible en continu' },
];

/* ── Input style for hero search ───────────────────────────── */
const heroInput = {
  '& .MuiOutlinedInput-root': {
    bgcolor: T.white, color: T.ink, borderRadius: T.radius.md,
    border: `1px solid ${T.border}`,
    '& fieldset': { border: 'none' },
    '&:hover': { borderColor: 'rgba(85,37,131,0.2)' },
    '&.Mui-focused': { borderColor: T.purple },
  },
  '& .MuiInputLabel-root': { color: T.muted },
  '& input': { color: T.ink },
};

const heroSelect = {
  '& .MuiOutlinedInput-root': {
    bgcolor: T.white, color: T.ink, borderRadius: T.radius.md,
    border: `1px solid ${T.border}`,
    '& fieldset': { border: 'none' },
    '&:hover': { borderColor: 'rgba(85,37,131,0.2)' },
  },
  '& .MuiInputLabel-root': { color: T.muted },
  '& .MuiSelect-icon': { color: T.muted },
};

const CarRentalSearch = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicleType, setVehicleType] = useState('auto');
  const [sortBy, setSortBy] = useState('recent');
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [filters, setFilters] = useState({ pickupDate: '', returnDate: '', category: '', transmission: '' });

  useEffect(() => { loadVehicles(); }, [vehicleType]); // eslint-disable-line

  const loadVehicles = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/car-rentals?type=${vehicleType}`);
      setVehicles(res.data.cars || []);
    } catch { setVehicles([]); }
    finally { setLoading(false); }
  };

  const today = todayMayotte();
  const hero = heroTitles[vehicleType];

  const handleTypeChange = (e, v) => {
    if (v) { setVehicleType(v); setFilters({ pickupDate: filters.pickupDate, returnDate: filters.returnDate, category: '', transmission: '' }); }
  };
  const handleFilterChange = (field, value) => {
    const u = { ...filters, [field]: value };
    if (field === 'pickupDate' && u.returnDate && value > u.returnDate) u.returnDate = value;
    setFilters(u);
  };

  const filteredVehicles = vehicles
    .filter(v => {
      if (filters.category && v.category !== filters.category) return false;
      if (filters.transmission && v.transmission !== filters.transmission) return false;
      if (v.pricePerDay < priceRange[0] || v.pricePerDay > priceRange[1]) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'price_asc') return a.pricePerDay - b.pricePerDay;
      if (sortBy === 'price_desc') return b.pricePerDay - a.pricePerDay;
      if (sortBy === 'rating') return b.rating - a.rating;
      return 0;
    });

  const rentalDays = (() => {
    if (!filters.pickupDate || !filters.returnDate) return 0;
    const d = Math.ceil((new Date(filters.returnDate) - new Date(filters.pickupDate)) / 86400000);
    return d > 0 ? d : 0;
  })();

  const getCategories = () => ({ auto: ['Berline', 'Économique', 'Citadine', 'SUV', 'Luxe', 'Utilitaire'], moto: ['Scooter', 'Quad', 'Cyclomoteur', 'Sportive'], bateau: ['Barque', 'Bateau à moteur', 'Jetski', 'Kayak'] }[vehicleType] || []);
  const getLabel = () => ({ auto: 'véhicule', moto: 'moto', bateau: 'bateau' }[vehicleType]);

  return (
    <Box sx={{ background: T.bg, minHeight: '100vh' }}>

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: 5, md: 7 }, pb: { xs: 6, md: 8 },
        position: 'relative', overflow: 'hidden',
        bgcolor: T.purple,
      }}>
        {/* Background image -- subtle */}
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `url("${heroImages[vehicleType]}")`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          opacity: 0.08, transition: 'opacity 0.4s ease',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>

          {/* Type tabs */}
          <ToggleButtonGroup value={vehicleType} exclusive onChange={handleTypeChange} sx={{
            mb: 4, flexWrap: 'wrap',
            '& .MuiToggleButton-root': {
              px: { xs: 1.8, sm: 2.5 }, py: 0.9,
              color: 'rgba(255,255,255,0.45)',
              border: '1px solid rgba(255,255,255,0.12)',
              textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
              borderRadius: `${T.radius.pill} !important`, mr: 0.8, gap: 0.8,
              transition: T.transition,
              '&.Mui-selected': {
                bgcolor: 'rgba(255,255,255,0.1)', color: 'white',
                border: '1px solid rgba(255,255,255,0.25)',
              },
              '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
            },
            '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': { borderLeft: '1px solid rgba(255,255,255,0.12)' },
          }}>
            {vehicleTypes.map(t => <ToggleButton key={t.value} value={t.value}>{t.icon} {t.label}</ToggleButton>)}
          </ToggleButtonGroup>

          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, letterSpacing: '0.12em', fontSize: '0.68rem', mb: 1.5, textTransform: 'uppercase' }}>
            {hero.overline}
          </Typography>
          <Typography sx={{
            color: 'white', fontWeight: 800, mb: 1,
            fontSize: { xs: '1.8rem', md: '2.8rem' },
            letterSpacing: '-0.02em', lineHeight: 1.1,
          }}>
            Louez{' '}
            <Box component="span" sx={{ color: T.gold }}>{hero.title}</Box>
            <br />à Mayotte
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 4, maxWidth: 440, fontSize: '0.92rem', lineHeight: 1.5 }}>
            {hero.subtitle}
          </Typography>

          {/* Search bar */}
          <Box sx={{
            p: { xs: 2, sm: 2.5 },
            bgcolor: T.bg,
            border: `1px solid rgba(85,37,131,0.06)`,
            borderRadius: T.radius.card,
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={vehicleType === 'auto' ? 2.5 : 3}>
                <TextField fullWidth size="small" label="Date de prise" type="date"
                  value={filters.pickupDate} onChange={e => handleFilterChange('pickupDate', e.target.value)}
                  InputLabelProps={{ shrink: true }} inputProps={{ min: today }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 15, color: T.muted }} /></InputAdornment> }}
                  sx={heroInput}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={vehicleType === 'auto' ? 2.5 : 3}>
                <TextField fullWidth size="small" label="Date de retour" type="date"
                  value={filters.returnDate} onChange={e => handleFilterChange('returnDate', e.target.value)}
                  InputLabelProps={{ shrink: true }} inputProps={{ min: filters.pickupDate || today }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 15, color: T.muted }} /></InputAdornment> }}
                  sx={heroInput}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={vehicleType === 'auto' ? 2.5 : 3}>
                <FormControl fullWidth size="small" sx={heroSelect}>
                  <InputLabel>Catégorie</InputLabel>
                  <Select value={filters.category} onChange={e => handleFilterChange('category', e.target.value)} label="Catégorie"
                    MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 260 } } }}>
                    <MenuItem value="">Toutes</MenuItem>
                    {getCategories().map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              {vehicleType === 'auto' && (
                <Grid item xs={12} sm={6} md={2.5}>
                  <FormControl fullWidth size="small" sx={heroSelect}>
                    <InputLabel>Transmission</InputLabel>
                    <Select value={filters.transmission} onChange={e => handleFilterChange('transmission', e.target.value)} label="Transmission"
                      MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 260 } } }}>
                      <MenuItem value="">Toutes</MenuItem>
                      <MenuItem value="Automatique">Automatique</MenuItem>
                      <MenuItem value="Manuelle">Manuelle</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={vehicleType === 'auto' ? 2 : 3}>
                <Button fullWidth variant="contained" size="large"
                  onClick={() => window.scrollTo({ top: 700, behavior: 'smooth' })}
                  sx={{ ...btnPrimary, py: 1.1 }}>
                  Rechercher
                </Button>
              </Grid>
            </Grid>

            {rentalDays > 0 && (
              <Box sx={{ mt: 1.5, py: 1, px: 1.5, bgcolor: 'rgba(85,37,131,0.04)', borderRadius: T.radius.sm, border: `1px solid ${T.border}` }}>
                <Typography variant="body2" sx={{ color: T.purple, fontWeight: 600, textAlign: 'center', fontSize: '0.82rem' }}>
                  <EventNoteIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  {rentalDays} jour{rentalDays > 1 ? 's' : ''} — du {new Date(filters.pickupDate).toLocaleDateString('fr-FR', { timeZone: TZ })} au {new Date(filters.returnDate).toLocaleDateString('fr-FR', { timeZone: TZ })}
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* ── AVANTAGES ── */}
      <Box sx={{ py: 3.5, bgcolor: T.white, borderBottom: `1px solid ${T.border}` }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} justifyContent="center">
            {advantages.map((a, i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Box display="flex" alignItems="center" gap={1.5} justifyContent="center">
                  <Box sx={{
                    width: 42, height: 42, borderRadius: T.radius.md,
                    bgcolor: 'rgba(85,37,131,0.05)',
                    border: `1px solid ${T.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {a.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: T.ink, fontSize: '0.88rem' }}>{a.title}</Typography>
                    <Typography sx={{ color: T.muted, fontSize: '0.75rem' }}>{a.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── RESULTATS ── */}
      <Box sx={{ py: 5 }}>
        <Container maxWidth="lg">
          {/* Toolbar */}
          <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3.5} flexWrap="wrap" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
            <Box>
              <Typography sx={{ fontWeight: 700, color: T.ink, fontSize: '1.15rem' }}>
                {loading ? 'Chargement...' : `${filteredVehicles.length} ${getLabel()}${filteredVehicles.length !== 1 ? 's' : ''}`}
              </Typography>
              <Typography sx={{ color: T.muted, fontSize: '0.82rem' }}>
                {filteredVehicles.length !== 1 ? 'disponibles' : 'disponible'} à la location
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button size="small" startIcon={<TuneIcon sx={{ fontSize: '1rem !important' }} />} onClick={() => setShowFilters(!showFilters)}
                sx={{
                  color: showFilters ? T.purple : T.sub,
                  border: `1px solid ${showFilters ? T.purple : 'rgba(85,37,131,0.15)'}`,
                  textTransform: 'none', fontWeight: 600, fontSize: '0.82rem',
                  borderRadius: T.radius.sm, bgcolor: showFilters ? 'rgba(85,37,131,0.04)' : T.white,
                  transition: T.transition,
                }}>
                Filtres
              </Button>
              <FormControl size="small" sx={{ minWidth: { xs: 130, sm: 150 } }}>
                <Select value={sortBy} onChange={e => setSortBy(e.target.value)} displayEmpty
                  startAdornment={<SortIcon sx={{ mr: 0.8, fontSize: 16, color: T.muted }} />}
                  MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 260 } } }}
                  sx={{
                    borderRadius: T.radius.sm, bgcolor: T.white,
                    border: `1px solid rgba(85,37,131,0.12)`, fontSize: '0.82rem',
                    '& .MuiSelect-select': { py: 0.7 }, '& fieldset': { border: 'none' },
                  }}>
                  <MenuItem value="recent">Plus récents</MenuItem>
                  <MenuItem value="price_asc">Prix croissant</MenuItem>
                  <MenuItem value="price_desc">Prix décroissant</MenuItem>
                  <MenuItem value="rating">Mieux notés</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Price filter */}
          {showFilters && (
            <Box sx={{ p: 2.5, mb: 3.5, borderRadius: T.radius.btn, bgcolor: T.white, border: `1px solid ${T.border}`, boxShadow: T.shadow }}>
              <Typography sx={{ mb: 1.5, color: T.ink, fontWeight: 600, fontSize: '0.85rem' }}>
                Fourchette de prix (EUR/jour)
              </Typography>
              <Box sx={{ px: 1.5 }}>
                <Slider value={priceRange} onChange={(e, v) => setPriceRange(v)} min={0} max={500} step={10}
                  valueLabelDisplay="auto" valueLabelFormat={v => `${v}EUR`}
                  sx={{
                    color: T.purple,
                    '& .MuiSlider-thumb': { bgcolor: T.white, border: `2px solid ${T.purple}`, width: 18, height: 18, boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
                    '& .MuiSlider-track': { bgcolor: T.purple },
                    '& .MuiSlider-rail': { bgcolor: 'rgba(85,37,131,0.12)' },
                  }}
                />
                <Box display="flex" justifyContent="space-between">
                  <Typography sx={{ color: T.muted, fontWeight: 600, fontSize: '0.75rem' }}>{priceRange[0]}EUR</Typography>
                  <Typography sx={{ color: T.muted, fontWeight: 600, fontSize: '0.75rem' }}>{priceRange[1]}EUR</Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Vehicle grid */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: T.purple }} size={32} /></Box>
          ) : (
            <Grid container spacing={2.5}>
              {filteredVehicles.map((v) => (
                <Grid item xs={12} sm={6} lg={4} key={v.id}>
                  <Box onClick={() => navigate(`/car-rental/${v.id}`)} sx={{
                    cursor: 'pointer', bgcolor: T.white,
                    border: `1px solid ${T.border}`,
                    borderRadius: T.radius.card, overflow: 'hidden',
                    transition: T.transition,
                    boxShadow: T.shadow,
                    '&:hover': { boxShadow: T.shadowHover, borderColor: 'rgba(85,37,131,0.15)' },
                  }}>
                    {/* Image */}
                    <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
                      <CardMedia component="img"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        image={v.image || heroImages[vehicleType]} alt={`${v.brand} ${v.model}`}
                      />

                      {/* Price tag */}
                      <Box sx={{ position: 'absolute', top: 10, right: 10, bgcolor: T.purple, borderRadius: T.radius.sm, px: 1.2, py: 0.5 }}>
                        <Typography sx={{ color: 'white', lineHeight: 1.1, fontSize: '0.95rem', fontWeight: 800 }}>{v.pricePerDay}EUR</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.55rem' }}>/jour</Typography>
                      </Box>

                      {/* Category chip */}
                      <Chip label={v.category} size="small"
                        sx={{ position: 'absolute', bottom: 10, left: 10, bgcolor: T.white, color: T.ink, fontWeight: 600, fontSize: '0.68rem', height: 24, borderRadius: '6px' }} />

                      {/* Rating */}
                      {v.rating > 0 && (
                        <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', alignItems: 'center', gap: 0.3, bgcolor: T.white, borderRadius: '6px', px: 0.8, py: 0.25 }}>
                          <StarIcon sx={{ fontSize: 13, color: T.gold }} />
                          <Typography sx={{ fontWeight: 700, color: T.ink, fontSize: '0.72rem' }}>{v.rating}</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Content */}
                    <Box sx={{ p: 2 }}>
                      <Typography sx={{ fontWeight: 700, color: T.ink, mb: 0.2, fontSize: '0.95rem' }}>
                        {v.brand} {v.model}
                      </Typography>
                      <Typography sx={{ color: T.muted, fontSize: '0.75rem', mb: 1.5 }}>
                        {v.year} -- {v.transmission}
                      </Typography>

                      {/* Specs */}
                      <Box display="flex" gap={0.8} mb={1.5} flexWrap="wrap">
                        {vehicleType === 'auto' && (<>
                          <Box display="flex" alignItems="center" gap={0.4} sx={{ bgcolor: 'rgba(85,37,131,0.04)', borderRadius: '6px', px: 0.8, py: 0.3 }}>
                            <PeopleIcon sx={{ fontSize: 13, color: T.purple }} />
                            <Typography sx={{ color: T.sub, fontWeight: 600, fontSize: '0.7rem' }}>{v.seats}</Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.4} sx={{ bgcolor: 'rgba(85,37,131,0.04)', borderRadius: '6px', px: 0.8, py: 0.3 }}>
                            <Luggage sx={{ fontSize: 13, color: T.purple }} />
                            <Typography sx={{ color: T.sub, fontWeight: 600, fontSize: '0.7rem' }}>{v.luggage}</Typography>
                          </Box>
                        </>)}
                        {vehicleType === 'moto' && (
                          <Box display="flex" alignItems="center" gap={0.4} sx={{ bgcolor: 'rgba(85,37,131,0.04)', borderRadius: '6px', px: 0.8, py: 0.3 }}>
                            <SpeedIcon sx={{ fontSize: 13, color: T.purple }} />
                            <Typography sx={{ color: T.sub, fontWeight: 600, fontSize: '0.7rem' }}>{v.seats} pl.</Typography>
                          </Box>
                        )}
                        {vehicleType === 'bateau' && (
                          <Box display="flex" alignItems="center" gap={0.4} sx={{ bgcolor: 'rgba(85,37,131,0.04)', borderRadius: '6px', px: 0.8, py: 0.3 }}>
                            <PeopleIcon sx={{ fontSize: 13, color: T.purple }} />
                            <Typography sx={{ color: T.sub, fontWeight: 600, fontSize: '0.7rem' }}>{v.seats} pass.</Typography>
                          </Box>
                        )}
                        <Box display="flex" alignItems="center" gap={0.4} sx={{ bgcolor: 'rgba(85,37,131,0.04)', borderRadius: '6px', px: 0.8, py: 0.3 }}>
                          <LocalGasStationIcon sx={{ fontSize: 13, color: T.purple }} />
                          <Typography sx={{ color: T.sub, fontWeight: 600, fontSize: '0.7rem' }}>{v.fuel}</Typography>
                        </Box>
                        {v.airConditioning && vehicleType === 'auto' && (
                          <Box display="flex" alignItems="center" gap={0.4} sx={{ bgcolor: 'rgba(85,37,131,0.04)', borderRadius: '6px', px: 0.8, py: 0.3 }}>
                            <AcUnitIcon sx={{ fontSize: 13, color: T.purple }} />
                            <Typography sx={{ color: T.sub, fontWeight: 600, fontSize: '0.7rem' }}>Clim</Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Total if dates selected */}
                      {rentalDays > 0 && (
                        <Box sx={{ py: 0.8, px: 1.2, mb: 1.5, borderRadius: T.radius.sm, bgcolor: 'rgba(85,37,131,0.03)', border: `1px solid ${T.border}` }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ color: T.muted, fontSize: '0.75rem' }}>{rentalDays} jour{rentalDays > 1 ? 's' : ''}</Typography>
                            <Typography sx={{ fontWeight: 700, color: T.purple, fontSize: '0.9rem' }}>{(v.pricePerDay * rentalDays).toFixed(0)}EUR</Typography>
                          </Box>
                        </Box>
                      )}

                      <Button fullWidth variant="contained" size="small"
                        endIcon={<ArrowForwardIcon sx={{ fontSize: '0.85rem !important' }} />}
                        sx={{ ...btnPrimary, py: 0.9, fontSize: '0.82rem' }}>
                        Voir les détails
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Empty state */}
          {!loading && filteredVehicles.length === 0 && (
            <Box textAlign="center" py={8}>
              <Box sx={{
                width: 80, height: 80, borderRadius: '50%',
                bgcolor: 'rgba(85,37,131,0.04)', border: `1px solid ${T.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 2.5,
              }}>
                {vehicleType === 'auto' && <DirectionsCarIcon sx={{ fontSize: 36, color: 'rgba(85,37,131,0.25)' }} />}
                {vehicleType === 'moto' && <TwoWheelerIcon sx={{ fontSize: 36, color: 'rgba(85,37,131,0.25)' }} />}
                {vehicleType === 'bateau' && <SailingIcon sx={{ fontSize: 36, color: 'rgba(85,37,131,0.25)' }} />}
              </Box>
              <Typography sx={{ fontWeight: 700, color: T.ink, mb: 0.5, fontSize: '1.05rem' }}>
                Aucun {getLabel()} disponible
              </Typography>
              <Typography sx={{ color: T.muted, mb: 3, maxWidth: 380, mx: 'auto', fontSize: '0.85rem' }}>
                Il n'y a pas encore de {getLabel()}s à louer dans cette catégorie. Soyez le premier !
              </Typography>
              <Button variant="contained" startIcon={<AddCircleOutlineIcon />}
                onClick={() => navigate('/car-rental/rent-my-vehicle')}
                sx={{ ...btnPrimary, px: 3.5 }}>
                Mettre mon {getLabel()} en location
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ py: 7, bgcolor: T.purple }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 800, color: 'white', mb: 1, fontSize: { xs: '1.3rem', md: '1.6rem' }, letterSpacing: '-0.01em' }}>
            Vous avez un véhicule ?
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 3.5, mx: 'auto', fontSize: '0.88rem', lineHeight: 1.5 }}>
            Mettez-le en location et générez des revenus. Inscription simple, vérification rapide, paiements sécurisés.
          </Typography>
          <Button variant="contained" size="large" startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigate('/car-rental/rent-my-vehicle')}
            sx={{ ...btnGold, px: 4, py: 1.3, fontSize: '0.92rem' }}>
            Commencer à louer
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default CarRentalSearch;
